import * as yup from 'yup';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import {
  activateSubscription,
  deactivateSubscription,
  editDonorSubscription,
  getDonorSubscription,
} from 'src/shared/services/donorSubscriptionService';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { useGlobals } from 'src/shared/contexts/appContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { FundraiserPicker } from './fundraiserPicker';
import { DonorSubscriptionResponse, FormDonorSubscriptionPutItem, MonthIntervalType } from '../models/donor';

type DonorSubscriptionsEditModalProps = {
  modalOpen: boolean;
  donorSubscriptionId: number;
  onSave: () => void;
  onClose: () => void;
};

export const DonorSubscriptionsEditModal = (props: DonorSubscriptionsEditModalProps) => {
  const { modalOpen, donorSubscriptionId, onSave, onClose } = props;

  const { t } = useTranslation(['gift', 'common']);

  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [donorSubscription, setDonorSubscription] = useState<DonorSubscriptionResponse>();

  const { setDateStamp } = useGlobals();

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const isMounted = useIsMounted();

  const [checked, setChecked] = React.useState(false);

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const DonorSubscriptionRequestPostSchema: Schema<FormDonorSubscriptionPutItem> = yup.object().shape({
    id: yup.number().required(),
    donor_id: yup.number().required(),
    fundraiser_id: yup.number().min(1, t('common:validationMessages.required')).required(t('common:validationMessages.required')),
    subscriptionStart: yup.string().required(t('common:validationMessages.required')).typeError(t('common:validationMessages.invalidFormat')),
    subscriptionLength: yup.number().notRequired(),
    continuous: yup.boolean().required(),
    amount: yup.number().min(10, t('gift:createDonor.amountValidation')).typeError(t('common:validationMessages.invalidFormat')).required(),
    monthlyFrequency: yup.mixed<MonthIntervalType>().oneOf([1, 3, 12], t('gift:createDonor.frequencyValidation')).required(),
  });

  const formMethods = useForm<FormDonorSubscriptionPutItem>({
    mode: 'onChange',
    resolver: yupResolver(DonorSubscriptionRequestPostSchema),
    defaultValues: {
      id: 0,
      donor_id: 0,
      fundraiser_id: 0,
      subscriptionStart: null,
      subscriptionLength: 0,
      continuous: false,
      amount: 0,
      monthlyFrequency: 1,
    },
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getDonorSubscription(donorSubscriptionId);
        if (isMounted()) {
          setDonorSubscription(result.data);
          reset({
            id: result.data.id,
            donor_id: result.data.donor_id,
            fundraiser_id: result.data.fundraiser.fundraiser_id,
            subscriptionStart: DateHelper.postDate(result.data.subscriptionStart),
            subscriptionLength: result.data.subscriptionLength,
            continuous: result.data.continuous,
            amount: result.data.amount,
            monthlyFrequency: result.data.monthlyFrequency,
          });
          setChecked(result.data.continuous);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [donorSubscriptionId, isMounted, reset]);

  const saveFormData = React.useCallback(
    async (data: FormDonorSubscriptionPutItem) => {
      const formData: FormDonorSubscriptionPutItem = {
        id: donorSubscriptionId,
        donor_id: data.donor_id,
        fundraiser_id: data.fundraiser_id,
        subscriptionStart: DateHelper.postDate(data.subscriptionStart),
        subscriptionLength: data.subscriptionLength,
        continuous: checked,
        amount: data.amount,
        active: true,
        monthlyFrequency: data.monthlyFrequency,
      };
      ConsoleHelper.log('formData to put', formData);
      setLoading(true);
      try {
        const res = await editDonorSubscription(formData);
        if (isMounted()) {
          toast.success(<>{t('gift:editDonor.saveSuccess')}</>);
          setServerErrors([]);
          if (res.data) {
            setLoading(false);
            onSave();
          }
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setLoading(false);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      }
    },
    [checked, donorSubscriptionId, isMounted, onSave, t],
  );

  const onSubmit = React.useCallback(
    (data: FormDonorSubscriptionPutItem) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const onFinishClick = async () => {
    if (!donorSubscription) return;
    setLoading(true);
    setConfirmFinishOpen(false);
    try {
      if (donorSubscription.active) {
        await deactivateSubscription(donorSubscription.id);
        if (isMounted()) toast.success(<>{t('gift:deactivateDonorSubscription')}</>);
      } else {
        await activateSubscription(donorSubscription.id);
        if (isMounted()) toast.success(<>{t('gift:activateDonorSubscription')}</>);
      }
      if (isMounted()) {
        setDateStamp(new Date());
        onClose();
      }
    } catch (e: any) {
      if (isMounted()) {
        ConsoleHelper.log('ERR delete donor', e);
        toast.error(e.data);
      }
    }
  };

  const handleFinish = async () => {
    if (donorSubscription?.active) {
      setConfirmFinishOpen(true);
    } else {
      onFinishClick();
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('gift:editDonorSubscription')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box ref={modalTop} sx={{ py: 2, border: (theme) => (serverErrors?.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}>
          <LoadingOverlay loading={loading} />
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ px: 2 }}>
                <GridWrapper>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledTextField
                      name='amount'
                      label={t('gift:paymentDetail.amount')}
                      inputProps={{ maxLength: 5 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      InputProps={{
                        endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledSelectBox
                      items={[
                        { label: t('gift:donorSubscriptions.monthly'), value: 1 },
                        { label: t('gift:donorSubscriptions.quarterly'), value: 3 },
                        { label: t('gift:donorSubscriptions.annually'), value: 12 },
                      ]}
                      name='monthlyFrequency'
                      label={t('gift:paymentDetail.paymentFrequency')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FundraiserPicker
                      initialValue={donorSubscription?.fundraiser_id || 0}
                      label={t('gift:paymentDetail.fundraiser')}
                      name='fundraiser_id'
                    />
                  </Grid>
                </GridWrapper>
              </Box>
              <Box sx={{ px: 2, mt: 2 }}>
                <GridWrapper>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledDatePicker label={t('gift:paymentDetail.subscriptionStart')} name='subscriptionStart' />
                  </Grid>
                  <Grid item xs>
                    <ControlledTextField
                      name='subscriptionLength'
                      label={t('gift:paymentDetail.subscriptionLength')}
                      inputProps={{ maxLength: 2 }}
                      disabled={checked}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <FormControlLabel
                      label={t('gift:paymentDetail.continuous')}
                      control={<Checkbox name='continuous' checked={checked} onChange={handleCheckbox} />}
                    />
                  </Grid>
                </GridWrapper>
              </Box>
            </form>
          </FormProvider>
          {/* Dialog bekräfta inaktivering */}
          <Dialog maxWidth='xs' open={confirmFinishOpen}>
            <DialogTitle>{t('gift:finishDonorSubscription.header')}</DialogTitle>
            <DialogContent>
              <Typography>{t('gift:finishDonorSubscription.description')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button autoFocus variant='outlined' onClick={() => setConfirmFinishOpen(false)}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' color='primary' disableElevation onClick={() => onFinishClick()}>
                {t('common:button.ok')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
      dialogActions={
        <Box display='flex' justifyContent='space-between' sx={{ width: '100%' }}>
          <>
            {donorSubscription && donorSubscription?.active && (
              <Button variant='outlined' color='error' onClick={handleFinish}>
                {t('gift:button.inactivate')}
              </Button>
            )}
            {donorSubscription && !donorSubscription?.active && (
              <Button variant='outlined' color='success' onClick={handleFinish}>
                {t('gift:button.activate')}
              </Button>
            )}
          </>
          <Box>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>
            <Button autoFocus variant='contained' color='primary' disableElevation onClick={onHandleSave}>
              {t('common:button.save')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
